<template>
    <div class="pd-renewal-fields">
        <p class="field-intro" v-if="intro">{{ intro }}</p>

        <!-- Stacked: one field per row, label left / input + hint right.
             For narrow containers (the 680px verification modal) where
             three columns jumble the labels and hints. -->
        <template v-if="stacked">
            <b-field label="Renewal date" horizontal :message="renewalHint" class="stacked-field">
                <b-input type="date" :value="value.pd_renewal_on || ''" @input="set('pd_renewal_on', $event)" :min="minDate" class="stacked-input"/>
            </b-field>
            <b-field label="Hours on file elsewhere" horizontal message="Clock hours toward this renewal you earned outside my247ed.com" class="stacked-field">
                <b-numberinput :value="numberOrNull(value.pd_external_hours)" @input="set('pd_external_hours', $event)" :min="0" :max="100000" :step="1" :controls="false" placeholder="0" class="stacked-input"/>
            </b-field>
            <b-field label="Hours needed" horizontal :message="targetHint" class="stacked-field">
                <b-numberinput :value="numberOrNull(value.pd_target_hours)" @input="set('pd_target_hours', $event)" :min="0" :max="100000" :step="5" :controls="false" :placeholder="String(defaultTarget)" class="stacked-input"/>
            </b-field>
        </template>

        <div v-else class="columns is-multiline mb-0">
            <div class="column is-12-mobile is-4">
                <b-field label="Certificate renewal date" :label-position="labelPosition" :message="renewalHint">
                    <b-input type="date" :value="value.pd_renewal_on || ''" @input="set('pd_renewal_on', $event)" :min="minDate" expanded/>
                </b-field>
            </div>
            <div class="column is-12-mobile is-4">
                <b-field label="Hours already on file elsewhere" :label-position="labelPosition" message="Clock hours toward this renewal you earned outside my247ed.com">
                    <b-numberinput :value="numberOrNull(value.pd_external_hours)" @input="set('pd_external_hours', $event)" :min="0" :max="100000" :step="1" :controls="false" expanded placeholder="0"/>
                </b-field>
            </div>
            <div class="column is-12-mobile is-4">
                <b-field label="Hours needed for renewal" :label-position="labelPosition" :message="targetHint">
                    <b-numberinput :value="numberOrNull(value.pd_target_hours)" @input="set('pd_target_hours', $event)" :min="0" :max="100000" :step="5" :controls="false" expanded :placeholder="String(defaultTarget)"/>
                </b-field>
            </div>
        </div>
    </div>
</template>

<script>
    import {PD_TARGET_DEFAULT, PD_TARGET_HINT} from "../../constants/profile";

    /**
     * The three certificate-renewal questions, shared by the profile
     * verification modal and the bundle builder's Your Needs step. Emits a
     * new meta object on every change (`v-model` friendly); the parent
     * merges it into the same `meta` it already saves through
     * POST app/users/profile-verification.
     */
    export default {
        name: "PdRenewalFields",
        props: {
            value: {type: Object, required: true},
            intro: {type: String, default: null},
            labelPosition: {type: String, default: 'inside'},
            // One field per row (label left, input + hint right) instead of
            // three columns; for narrow containers like the verification modal
            stacked: {type: Boolean, default: false},
        },
        data() {
            return {defaultTarget: PD_TARGET_DEFAULT, targetHint: PD_TARGET_HINT};
        },
        computed: {
            minDate() {
                return new Date().toISOString().slice(0, 10);
            },
            renewalHint() {
                return this.value.pd_renewal_on ? '' : 'Optional — lets your dashboard track progress toward renewal';
            }
        },
        methods: {
            numberOrNull(v) {
                if (v === null || v === undefined || v === '') return null;
                const n = parseFloat(v);
                return Number.isNaN(n) ? null : n;
            },
            set(key, v) {
                this.$emit('input', {...this.value, [key]: (v === '' || v === undefined) ? null : v});
            }
        }
    };
</script>

<style lang="scss" scoped>
    .field-intro {
        font-size: 0.9rem;
        color: #666;
        margin-bottom: 0.75rem;
    }

    // Horizontal fields: keep the inputs a sensible width rather than
    // stretching to the row; hints sit under the input
    .stacked-field {
        ::v-deep .field-label {
            flex-grow: 2;
            text-align: left;
            padding-top: 0.4em;
        }

        ::v-deep .stacked-input {
            max-width: 14rem;
        }

        ::v-deep .help {
            font-size: 0.75rem;
            color: #7a7a7a;
        }
    }
</style>
